import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { HttpErrorResponse } from '@angular/common/http';
import { LucideAngularModule, Globe } from 'lucide-angular';

import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../core/services/auth.service';
import { LanguageCode, LANGUAGES, LanguageService } from '../../core/services/language.service';

/**
 * English or ಕನ್ನಡ, from anywhere in the app.
 *
 * Switching is immediate and local — the signal changes, every screen
 * re-renders, and the next request carries the new `Accept-Language`, so even
 * server-written refusals follow. Saving the choice to the account happens
 * afterwards and quietly: if it fails, the person still gets the language they
 * asked for, they just get English again on their next device.
 *
 * Used twice: in the top bar, and on the sign-in screen, which sits outside the
 * shell. Somebody who cannot read English has to be able to switch before they
 * can sign in at all.
 */
@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, TranslocoDirective],
  template: `
    <div class="relative inline-flex items-center" *transloco="let t">
      <span class="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
        <lucide-icon [img]="Globe" class="w-4 h-4 text-mutedText"></lucide-icon>
      </span>
      <select
        [attr.aria-label]="t('shell.language')"
        [ngModel]="language.current()"
        (ngModelChange)="choose($event)"
        class="appearance-none pl-8 pr-7 py-1.5 rounded-control border border-borderLight
               bg-surface text-sm font-medium text-slateText cursor-pointer
               hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary
               focus:border-primary transition-colors">
        @for (option of languages; track option.code) {
          <option [value]="option.code">{{ option.label }}</option>
        }
      </select>
    </div>
  `
})
export class LanguageSwitcherComponent {
  readonly language = inject(LanguageService);
  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);

  readonly Globe = Globe;
  readonly languages = LANGUAGES;

  choose(code: LanguageCode): void {
    this.language.use(code);
    // Remember it on the account, so the next device starts here too. Nobody
    // is told if this fails: the language on screen already changed, which is
    // what was asked for, and a toast about a preference would be noise.
    if (this.auth.isAuthenticated()) {
      this.api.updateMyProfile({ language: code }).subscribe({
        error: (error: HttpErrorResponse) => console.warn('Language not saved', error.status)
      });
    }
  }
}
